import { openAuthedChatUrl } from '@lib/network/chats';
import { getPodcastCreateUrl, listPodcastInstances, PodcastInstance } from '@lib/network/podcasts';
import { verbMenuIcon } from '@lib/verbs';
import { Skeleton } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { AppPage } from '@pages/AppPage';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { PodcastListItem } from './components/PodcastListItem/PodcastListItem';
import css from './PodcastsPage.module.scss';

const PAGE_SIZE = 25;
// Whether the chat web app is reachable at all. The actual create URL
// is computed at click time so it carries a short-lived exchange token
// via `openAuthedChatUrl` (avoids the chat→app→chat handoff bounce).
const CHAT_CONFIGURED = !!process.env.CHAT_URL;

/**
 * Standalone listing of every podcast project the user has. Replaces
 * the "See all" external bounce on the Workspace's preview row.
 *
 * v1: simple "load more" pagination — small data sets right now, no
 * need for virtual scrolling. We can swap to infinite-scroll +
 * virtualisation later if lists grow into the hundreds.
 */
export const PodcastsPage = observer(() => {
  const [instances, setInstances] = useState<PodcastInstance[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    listPodcastInstances({ pageSize: PAGE_SIZE, start: 0, signal: controller.signal })
      .then((data) => {
        setInstances(data);
        setHasMore(data.length === PAGE_SIZE);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch podcast projects:', e);
        setError(e);
        setInstances([]);
      });
    return () => controller.abort();
  }, []);

  const handleLoadMore = async () => {
    if (!instances || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = await listPodcastInstances({ pageSize: PAGE_SIZE, start: instances.length });
      setInstances([...instances, ...next]);
      if (next.length < PAGE_SIZE) setHasMore(false);
    } catch (e) {
      console.error('Failed to load more podcast projects:', e);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <AppPage header="Podcasts">
      <div className={css.container}>
        <div className={css.headerStrip}>
          <ActionButton
            variant="primary"
            disabled={!CHAT_CONFIGURED}
            onClick={() => {
              const url = getPodcastCreateUrl();
              if (url)
                void openAuthedChatUrl(url, '_self', {
                  surface: 'podcasts-page',
                  control: 'header-button',
                });
            }}
          >
            {/* Same capability as the rails' clip verb, so it takes the catalog's glyph — the
                sparkle is reserved for the agent. Not its label: the catalog's names a media,
                and this button starts a project with none picked yet. */}
            {verbMenuIcon('clip-podcast')}
            &nbsp;Clip as a podcast
          </ActionButton>
        </div>

        <div className={css.scrollableContent}>
          {error && instances?.length === 0 && (
            <div className={css.errorPanel}>
              <strong>Couldn't load your podcast projects.</strong>
              <span>{error.message}</span>
            </div>
          )}

          {instances === null && (
            <div className={css.list}>
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={128}
                  sx={{ borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.04)' }}
                />
              ))}
            </div>
          )}

          {instances && instances.length === 0 && !error && (
            <div className={css.empty}>
              <h2 className={css.emptyTitle}>No podcast projects yet</h2>
              <p className={css.emptyText}>Start one from the Workspace's Podcast Clipping card.</p>
            </div>
          )}

          {instances && instances.length > 0 && (
            <div className={css.list}>
              {instances.map((instance) => (
                <PodcastListItem key={instance._id} instance={instance} />
              ))}
            </div>
          )}

          {hasMore && instances && instances.length > 0 && (
            <div className={css.loadMoreRow}>
              <ActionButton variant="primary" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : 'Load more'}
              </ActionButton>
            </div>
          )}
        </div>
      </div>
    </AppPage>
  );
});
